<template>
  <TPill
    v-for="item of items"
    :key="item.slug"
   
    :is-active="item.isActive"
    @click="item.onClick"
  >
    <template #icon>
      <TIcon :icon="['fal', item.icon]" />
    </template>
    {{ t(item.slug) }}
  </TPill>
</template>

<script setup lang="ts">
import type { IconName } from '@fortawesome/fontawesome-svg-core'
import type { AMENITIES } from '~/constants/amenities'
import { BUDGET_FRIENDLY_MAX_PRICE, BUDGET_FRIENDLY_MIN_PRICE } from '~/constants/search'

const { searchData, navigateToRvSearch } = useSearch()
const { t } = useI18n()
const { isInUs } = useGeolocation()
const { showThanksGivingBanner } = useExperiment()

type QuickFiltersItems = {
  slug: 'budgetFriendly' | 'delivery' | 'familyFriendly' | 'festivalFriendly' | 'instantBook' | 'petFriendly' | 'thanksgiving' | 'unlimitedMileage'
  icon: IconName
  isActive: boolean
  onClick: () => void
}

const items = computed((): QuickFiltersItems[] => {
  const items: QuickFiltersItems[] = [
    {
      slug: 'delivery',
      icon: 'route',
      onClick: handleDeliveryFilter,
      isActive: searchData.value.delivery,
    },
    {
      slug: 'unlimitedMileage',
      icon: 'gauge-circle-bolt',
      onClick: handleUnlimitedMileageFilter,
      isActive: searchData.value.unlimitedMileage,
    },
    {
      slug: 'budgetFriendly',
      icon: 'piggy-bank',
      onClick: handleBudgetFriendlyFilter,
      isActive: isBudgetFriendly(searchData.value.minPrice, searchData.value.maxPrice),
    },
    {
      slug: 'petFriendly',
      icon: 'paw-simple',
      onClick: handlePetFriendlyFilter,
      isActive: searchData.value.guests?.petFriendly ?? false,
    },
    {
      slug: 'familyFriendly',
      icon: 'family-pants',
      onClick: handleFamilyFriendlyFilter,
      isActive: searchData.value.amenities?.includes('Family_kidfriendly'),
    },
    {
      slug: 'festivalFriendly',
      icon: 'calendar-star',
      onClick: handleFestivalFriendlyFilter,
      isActive: searchData.value.festivalFriendly,
    },
    {
      slug: 'instantBook',
      icon: 'bolt',
      onClick: handleInstantBookFilter,
      isActive: searchData.value.instantBook,
    },
  ]

  if (showThanksGivingBanner.value) {
    items.unshift({
      slug: 'thanksgiving',
      icon: 'leaf-maple',
      onClick: handleThanksgivingFilter,
      isActive: isThanksgivingSelected(isInUs.value, String(searchData.value.dates?.start), String(searchData.value.dates?.end)),
    })
  }

  return items
})

async function handleDeliveryFilter() {
  await navigateToRvSearch({
    Delivery: !searchData.value.delivery,
  })
}

async function handleUnlimitedMileageFilter() {
  await navigateToRvSearch({
    UnlimitedMileage: !searchData.value.unlimitedMileage,
  })
}

async function handleBudgetFriendlyFilter() {
  const isActive = isBudgetFriendly(searchData.value.minPrice, searchData.value.maxPrice)
  const MinPrice = isActive ? undefined : BUDGET_FRIENDLY_MIN_PRICE
  const MaxPrice = isActive ? undefined : BUDGET_FRIENDLY_MAX_PRICE

  await navigateToRvSearch({
    MinPrice,
    MaxPrice,
  })
}

async function handlePetFriendlyFilter() {
  await navigateToRvSearch({
    PetFriendly: !searchData.value.guests?.petFriendly,
  })
}

async function handleFamilyFriendlyFilter() {
  const amenity: AMENITIES = 'Family_kidfriendly'
  const current = searchData.value.amenities ?? []
  const Amenities: AMENITIES[] = current.includes(amenity)
    ? current.filter((a) => a !== amenity)
    : [...current, amenity]

  await navigateToRvSearch({ Amenities })
}

async function handleFestivalFriendlyFilter() {
  await navigateToRvSearch({
    FestivalFriendly: !searchData.value.festivalFriendly,
  })
}

async function handleInstantBookFilter() {
  await navigateToRvSearch({
    InstantBook: !searchData.value.instantBook,
  })
}

async function handleThanksgivingFilter() {
  const isActive = isThanksgivingSelected(isInUs.value, String(searchData.value.dates?.start), String(searchData.value.dates?.end))
  const StartDate = isActive ? undefined : selectThanksgivingDates(isInUs.value).start
  const EndDate = isActive ? undefined : selectThanksgivingDates(isInUs.value).end
  await navigateToRvSearch({
    StartDate,
    EndDate,
  })
}
</script>

<i18n lang="json">
{
  "en": {
    "thanksgiving": "Thanksgiving",
    "delivery": "Delivery",
    "unlimitedMileage": "Unlimited Mileage",
    "budgetFriendly": "Budget Friendly",
    "petFriendly": "Pet Friendly",
    "familyFriendly": "Family Friendly",
    "festivalFriendly": "Festivals",
    "instantBook": "Instant Book"
  },
  "fr": {
    "thanksgiving": "Action de grâce",
    "delivery": "Livraison",
    "unlimitedMileage": "Kilométrage illimité",
    "budgetFriendly": "Économique",
    "petFriendly": "Animaux admis",
    "familyFriendly": "Pour famille",
    "festivalFriendly": "Festivals",
    "instantBook": "Réservation instantanée"
  }
}
</i18n>
